<script lang="ts">
  /**
   * Triage — vider la file d'un geste, un fil à la fois.
   *
   * La file est PHOTOGRAPHIÉE à l'entrée, comme le chaînage des fiches
   * société : chaque décision retire un fil de la boîte, et sans cette photo
   * l'ordre se déroberait sous le pouce à chaque tap. C'est la même leçon
   * qu'au CRM, et elle vaut partout où une action vide la liste qu'on
   * parcourt.
   */

  import { gmailState, type InboxRow } from "../gmail/store.svelte";
  import { archiveThread, markRead } from "../gmail/sync";
  import { toggleLater, flagsState } from "../gmail/threadFlags.svelte";
  import { openSnooze } from "./snoozeStore.svelte";
  import { openReplyCompose } from "../gmail/compose";
  import { getDisplayLabels } from "../gmail/labels.svelte";
  import { extractAttachments } from "../gmail/parser";
  import { showToast } from "../snackbar/store.svelte";
  import { haptic } from "../gestures/haptic";
  import Icon from "./shared/Icon.svelte";

  interface Props {
    /** Sortir du triage — retourne à la Boîte. */
    onQuit: () => void;
  }
  let { onQuit }: Props = $props();

  // === File ================================================================
  let queue = $state<string[]>([]);
  let idx = $state(0);
  let started = $state(false);

  /** Les fils que la Boîte montrerait : ni reportés, ni mis de côté. */
  function eligible(): InboxRow[] {
    const now = Date.now();
    return gmailState.rows.filter((r) => {
      const f = flagsState.byId[r.thread.id];
      const snoozed = !!f?.snoozedUntil && f.snoozedUntil > now;
      return !snoozed && f?.later !== true;
    });
  }

  function start() {
    queue = eligible().map((r) => r.thread.id);
    idx = 0;
    started = true;
  }

  const current = $derived.by<InboxRow | null>(() => {
    const id = queue[idx];
    if (!id) return null;
    return gmailState.rows.find((r) => r.thread.id === id) ?? null;
  });

  const remaining = $derived(Math.max(0, queue.length - idx));
  const done = $derived(started && idx >= queue.length);

  /** Quatorze segments quel que soit le volume : la barre doit rester lisible. */
  const SEGMENTS = 14;
  const filled = $derived(
    queue.length === 0 ? 0 : Math.round((idx / queue.length) * SEGMENTS),
  );

  function advance() {
    idx += 1;
  }

  // === Actions =============================================================
  function act(kind: "archive" | "later" | "snooze" | "reply") {
    const row = current;
    if (!row) return;
    const id = row.thread.id;
    haptic("medium");
    switch (kind) {
      case "archive":
        void archiveThread(id);
        showToast({ message: "Archivé" });
        advance();
        return;
      case "later":
        void toggleLater(id);
        showToast({ message: "Marqué « À traiter »" });
        advance();
        return;
      case "snooze":
        openSnooze(id);
        advance();
        return;
      case "reply": {
        const last = row.thread.messages[row.thread.messages.length - 1];
        if (last) openReplyCompose(last, id);
        return;
      }
    }
  }

  function openFull() {
    const row = current;
    if (!row) return;
    void markRead(row.thread.id);
    gmailState.selectedThreadId = row.thread.id;
  }

  // === Affichage ===========================================================
  function initials(row: InboxRow): string {
    const src = row.fromName?.trim() || row.fromEmail || "?";
    const parts = src.split(/[\s.@]+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  function domainOf(row: InboxRow): string {
    return (row.fromEmail ?? "").split("@")[1]?.toLowerCase() ?? "";
  }

  function timeOf(row: InboxRow): string {
    return new Date(row.lastDate).toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  /**
   * Dédoublonnage par nom : le même fichier réapparaît à chaque réponse
   * citée, et une carte de triage qui affiche quatre fois `stock.xlsx` fait
   * croire à quatre pièces jointes.
   */
  const attachments = $derived.by(() => {
    if (!current) return [];
    const seen = new Set<string>();
    const out = [];
    for (const m of current.thread.messages) {
      for (const a of extractAttachments(m)) {
        const key = a.filename.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(a);
      }
    }
    return out;
  });

  function kindOf(filename: string): string {
    const ext = filename.split(".").pop()?.toUpperCase() ?? "";
    if (["XLSX", "XLS", "CSV"].includes(ext)) return "XLSX";
    if (ext === "PDF") return "PDF";
    if (["PNG", "JPG", "JPEG", "GIF", "WEBP"].includes(ext)) return "IMG";
    if (["DOC", "DOCX"].includes(ext)) return "DOC";
    if (["ZIP", "RAR", "7Z"].includes(ext)) return "ZIP";
    return ext.slice(0, 4) || "FIC";
  }
</script>

<div class="triage">
  <header>
    <div class="title-row">
      <div>
        <h1>Triage</h1>
        <div class="meta">
          {#if started}
            {Math.min(idx + 1, queue.length)} sur {queue.length} · {remaining} restant{remaining > 1 ? "s" : ""}
          {:else}
            {eligible().length} fil{eligible().length > 1 ? "s" : ""} en file
          {/if}
        </div>
      </div>
      {#if started}
        <button class="quit" onclick={onQuit}>Quitter</button>
      {/if}
    </div>

    {#if started}
      <div class="progress" aria-hidden="true">
        {#each Array(SEGMENTS) as _, i (i)}
          <span class="seg" class:on={i < filled}></span>
        {/each}
      </div>
    {/if}
  </header>

  <div class="body">
    {#if !started}
      <div class="intro">
        <p>Un fil à la fois, quatre décisions.</p>
        <p class="sub">
          La file est figée au démarrage : ce que tu tranches ne change pas
          l'ordre de ce qui suit.
        </p>
        <button class="go" onclick={start} disabled={eligible().length === 0}>
          Commencer
        </button>
      </div>
    {:else if done || !current}
      <div class="intro">
        <p>File terminée.</p>
        <button class="go" onclick={onQuit}>Retour à la Boîte</button>
      </div>
    {:else}
      {@const row = current}
      {#key row.thread.id}
        {@const chips = getDisplayLabels(row.thread.messages)}
        <article class="card">
          <div class="band">
            <span class="avatar" aria-hidden="true">{initials(row)}</span>
            <span class="who">
              <span class="name">{row.fromName?.trim() || row.fromEmail}</span>
              {#if domainOf(row)}<span class="domain">{domainOf(row)}</span>{/if}
            </span>
            <span class="when">
              <span class="hour">{timeOf(row)}</span>
              <span class="msgs">{row.thread.messages.length} msg</span>
            </span>
          </div>

          <div class="content">
            <h2>{row.subject}</h2>
            {#if chips.length > 0}
              <div class="chips">
                {#each chips as c (c.id)}<span class="mfx-chip">{c.name}</span>{/each}
              </div>
            {/if}
            <p class="excerpt">{row.snippet}</p>

            {#if attachments.length > 0}
              <div class="atts">
                {#each attachments.slice(0, 4) as a (a.attachmentId)}
                  <span class="att">
                    <span class="k">{kindOf(a.filename)}</span>
                    {a.filename}
                  </span>
                {/each}
              </div>
            {/if}
          </div>
        </article>

        <button class="full" onclick={openFull}>Ouvrir le fil complet</button>
      {/key}
    {/if}
  </div>

  {#if started && !done && current}
    <div class="actions">
      <button class="act" onclick={() => act("archive")}>
        <Icon name="archive" size={17} /><span>Archiver</span>
      </button>
      <button class="act" onclick={() => act("later")}>
        <Icon name="star" size={17} /><span>À traiter</span>
      </button>
      <button class="act" onclick={() => act("snooze")}>
        <Icon name="clock" size={17} /><span>Reporter</span>
      </button>
      <button class="act primary" onclick={() => act("reply")}>
        <Icon name="reply" size={17} /><span>Répondre</span>
      </button>
    </div>
  {/if}
</div>

<style>
  .triage { min-height: 100%; background: var(--canvas); }

  header {
    background: var(--bg);
    border-bottom: 1px solid var(--border-subtle);
    padding: calc(env(safe-area-inset-top) + 14px) 16px 12px;
  }
  .title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }
  h1 {
    margin: 0;
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    font-size: 31px;
    line-height: 1;
    letter-spacing: -0.015em;
  }
  .meta {
    margin-top: 7px;
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--fg-subtle);
  }
  .quit {
    flex-shrink: 0;
    height: 30px;
    padding: 0 12px;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-btn);
    font-size: 12px;
    color: var(--fg-muted);
  }

  .progress {
    display: flex;
    gap: 3px;
    margin-top: 13px;
  }
  .seg {
    flex: 1;
    height: 3px;
    border-radius: 2px;
    background: var(--bg-hover);
  }
  .seg.on { background: var(--accent); }

  /* Marge basse : barre d'action + tab bar. */
  .body { padding: 14px 12px 150px; }

  .card {
    background: var(--bg);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-card);
    overflow: hidden;
    animation: mfx-up 220ms var(--ease-ios) both;
  }

  .band {
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 14px 15px 12px;
    border-bottom: 1px solid var(--border-subtle);
  }
  .avatar {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: var(--accent-soft);
    color: var(--accent);
    font-size: 13px;
    font-weight: 600;
  }
  .who { flex: 1; min-width: 0; }
  .name {
    display: block;
    font-size: 13.5px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .domain {
    display: block;
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--fg-subtle);
  }
  .when {
    flex-shrink: 0;
    text-align: right;
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
  }
  .hour { display: block; font-size: 10.5px; color: var(--fg-muted); }
  .msgs { display: block; font-size: 9.5px; color: var(--fg-subtle); }

  .content { padding: 14px 15px 15px; }
  h2 {
    margin: 0;
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    font-size: 20px;
    line-height: 1.22;
    letter-spacing: -0.01em;
    text-wrap: pretty;
  }
  .chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 10px; }
  .excerpt {
    margin: 12px 0 0;
    font-size: 13px;
    line-height: 1.62;
    color: var(--fg-muted);
  }

  .atts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
  .att {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    height: 26px;
    padding: 0 9px;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    background: var(--bg-elev);
    font-size: 11.5px;
    color: var(--fg-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .att .k {
    font-family: var(--font-mono);
    font-size: 9px;
    font-weight: 500;
    color: var(--accent);
  }

  .full {
    display: block;
    width: 100%;
    margin-top: 12px;
    padding: 11px;
    border: 1px dashed var(--border);
    border-radius: var(--radius-lg);
    font-size: 12.5px;
    color: var(--fg-muted);
  }

  .intro { padding: 3rem 1rem; text-align: center; }
  .intro p {
    margin: 0;
    font-family: var(--font-serif);
    font-style: italic;
    font-size: 22px;
    color: var(--fg-subtle);
  }
  .intro .sub {
    margin-top: 10px;
    font-family: var(--font-sans);
    font-style: normal;
    font-size: 12.5px;
    line-height: 1.5;
  }
  .go {
    margin-top: 20px;
    height: 44px;
    padding: 0 22px;
    border-radius: var(--radius-lg);
    background: var(--accent);
    color: var(--accent-fg);
    font-size: 14px;
    font-weight: 600;
  }
  .go:disabled { opacity: 0.45; }

  .actions {
    position: fixed;
    left: 0;
    right: 0;
    /* Au-dessus de la tab bar, qui reste visible. */
    bottom: calc(env(safe-area-inset-bottom) + 52px);
    z-index: 15;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 7px;
    padding: 10px 12px 12px;
    background: color-mix(in srgb, var(--bg) 92%, transparent);
    backdrop-filter: saturate(180%) blur(20px);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    border-top: 1px solid var(--border-subtle);
  }
  .act {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    height: 56px;
    border: 1px solid var(--border-subtle);
    border-radius: 11px;
    background: var(--bg-elev);
    color: var(--fg);
    font-size: 10.5px;
    font-weight: 600;
  }
  .act:active { background: var(--bg-hover); }
  .act.primary {
    background: var(--accent);
    border-color: var(--accent);
    color: var(--accent-fg);
  }
</style>
